import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ImageService } from '../../services/image.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, CdkDragStart, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ConfirmSwitchDialogComponent } from '../confirm-switch-dialog/confirm-switch-dialog.component';
import { ToasterService } from '../../services/toaster.service';

interface UploadedImage {
  filename: string;
  imageData: string;
}
interface ImageDetails {
  uuid: string;
  button_text: string;
  description: string;
  end_date: Date;
  image_type: string;
  image_url: string;
  language: string;
  read_more_link: string;
  start_date: Date;
  title: string;
  rank_key: string;
  status?: string; 
}

export interface ImageRankPayload {
  previous: {
    uuid: string;
    rank: string;
  };
  next?: {
    uuid: string;
    rank: string;
  };
}
@Component({
  selector: 'app-upload-image-component',
  templateUrl: './upload-image-component.component.html',
  styleUrls: ['./upload-image-component.component.css'],
  providers: [DatePipe]
})

export class UploadImageComponentComponent implements OnInit {

  dateFormat: string;

  imageDataInfo: ImageDetails[];
  uuid: string;
  selectedImageType: string = 'explorer';
  selectedLanguage: string = 'en';
  showPreview: boolean = false;
  currentView: string = 'list-view';
  showProgress: boolean = false;
  progress: number = 0;
  fileName: string ;
  uploadedImage: string | ArrayBuffer | null;
  uploadedImages: UploadedImage[];
  chunkedImages: any[][];
  searchQuery: string ;
  filteredImageDataInfo: ImageDetails[];
  errorMessage: boolean ;

  constructor(private router: Router, private route: ActivatedRoute, private imageService: ImageService, private dialog: MatDialog, private cdr: ChangeDetectorRef,private toaster: ToasterService) { }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.selectedImageType = params['imageType'] || 'explorer';  // Default to explorer if not provided
      this.selectedLanguage = params['language'] || 'en';          // Default to English if not provided
      this.fetchImages(this.selectedImageType, this.selectedLanguage);
    });
    this.dateFormat = 'dd MMM yyyy h:mm:ss a zzzz'
  }

  fetchImages(imageType: string = this.selectedImageType, language: string = this.selectedLanguage): void {
    this.imageService.listImages(imageType, language).subscribe(
      (imageData: ImageDetails[]) => {
        this.imageDataInfo = imageData.map(image => ({
          ...image,
          status: this.calculateStatus(image.start_date, image.end_date)
        }));
        this.filteredImageDataInfo = [...this.imageDataInfo];
      },
      error => {
        console.error('Failed to fetch images', error);
        if(error.status === 403){
        this.toaster.openDialogBox("You are unauthorized to access this page",true);
        }
      }
    );
    this.errorMessage = false;
    setTimeout(() => {
      if (this.filteredImageDataInfo.length === 0) {
        this.errorMessage = true;
      }
    }, 1000);
  }
  
  calculateStatus(startDate: Date, endDate: Date): string {
    const now = new Date();
  
    if (now < new Date(startDate)) {
      return "Scheduled";
    } else if (now > new Date(endDate)) {
      return "Expired";
    } else {
      return "Active";
    }
  }
  
  filterImages(): void {
    this.router.navigate(['/upload', this.selectedImageType, this.selectedLanguage]);
    this.fetchImages(this.selectedImageType, this.selectedLanguage);
  }

  viewType(view: string) {
    this.currentView = view;
  }

  photoUpload() {
    this.showPreview = true;
    this.router.navigate(['/photo-upload']);
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    const files = event.dataTransfer?.files;
    if (files && files.length) {
      const file = files[0];
      if (this.isValidFileType(file)) {
        this.fileName = file.name;
        this.previewFile(file);
        this.uploadFile(file);
      } else {
        this.toaster.openDialogBox('Invalid file type. Please upload a .jpeg, .jpg, or .png file.',false);
      }
    }
  }

  onFileOver(event: DragEvent) {
    event.preventDefault();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const file = input.files[0];
      this.fileName = file.name;
      this.previewFile(file);
      this.uploadFile(file);
    }
  }

  isValidFileType(file: File): boolean {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    return allowedTypes.includes(file.type);
  }

  previewFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      this.uploadedImage = reader.result;
    };
    reader.readAsDataURL(file);
  }

  uploadFile(file: File) {
    this.showProgress = true;
    const uploadInterval = setInterval(() => {
      this.progress += 10;
      if (this.progress >= 100) {
        clearInterval(uploadInterval);
        this.showProgress = false;
        this.showPreview = true;
        this.imageService.setImage(this.uploadedImage);
        this.router.navigate(['/image-preview']);
      }
    }, 500);
  }

  abortUpload() {
    this.showProgress = false;
    this.progress = 0;
    this.uploadedImage = null;
    this.fileName = '';
  }

  deleteImage(imageUrl: string) {
    const index = this.imageDataInfo.findIndex(image => image.image_url === imageUrl);
    const imageTitle = this.imageDataInfo[index].title;
    const id = this.imageDataInfo[index].uuid;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: { confirmationText: 'Are you sure you want to delete this image?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'yes') {
        this.imageService.deleteImage(id).subscribe(
          () => {
            this.imageDataInfo.splice(index, 1);
            this.filteredImageDataInfo = [...this.imageDataInfo];
            this.toaster.openDialogBox('Image deleted successfully',false);
          },
          error => {
            console.error('Failed to delete image', error);
            if(error.status === 403){
            this.toaster.openDialogBox("You don't have the required permission",false)
            }
          }
        );
      }
    });
  }

  updateImage(imageUrl: string) {
    const index = this.imageDataInfo.findIndex(image => image.image_url === imageUrl);
    if (index !== -1) {
      const selectedImage = this.imageDataInfo[index];
      this.router.navigate(['/update-image'], { state: { selectedImage } });
    }
  }

  searchImages(): void {
    const query = this.searchQuery.toLowerCase();
    this.filteredImageDataInfo = this.imageDataInfo.filter(image =>
      image.title.toLowerCase().includes(query)
    );
    if (this.filteredImageDataInfo.length === 0) {
      this.errorMessage = true;
    } else {
      this.errorMessage = false;
    }
  }

  onImageDrop(event: CdkDragDrop<string[]>) {
    const previousIndex = event.previousIndex;
    const currentIndex = event.currentIndex;

    moveItemInArray(this.imageDataInfo, event.previousIndex, event.currentIndex);

    if (previousIndex !== currentIndex) {
      let previousRank = "";
      let nextRank = "";
      let previousUUID = "";
      let nextUUID = "";

      if (currentIndex === 0) {
        previousRank = this.imageDataInfo[currentIndex + 1].rank_key;
        previousUUID = this.imageDataInfo[currentIndex + 1].uuid;
      } else if (currentIndex === this.imageDataInfo.length - 1) {
        nextRank = this.imageDataInfo[currentIndex - 1].rank_key;
        nextUUID = this.imageDataInfo[currentIndex - 1].uuid;
      } else {
        nextRank = this.imageDataInfo[currentIndex - 1].rank_key;
        nextUUID = this.imageDataInfo[currentIndex - 1].uuid;
        previousRank = this.imageDataInfo[currentIndex + 1].rank_key;
        previousUUID = this.imageDataInfo[currentIndex + 1].uuid;
      }

      const payload: ImageRankPayload = {
        previous: {
          uuid: previousUUID,
          rank: previousRank
        },
        next: {
          uuid: nextUUID,
          rank: nextRank
        }
      };

      this.imageService.updateImageRank(this.imageDataInfo[currentIndex].uuid, payload).subscribe(
        response => {
          this.filterImages();
        },
        error => {
          console.error('Failed to update rank:', error);
          if(error.status === 403){
            this.toaster.openDialogBox("You don't have the required permission",false)
          }
        }
      );
    } else {
      console.error("The image was dropped in the same position. No changes made.");
    }
  }

  onDragStarted(event: CdkDragStart<any>) {
    if (this.currentView === 'grid-view') {
      const dialogRef = this.dialog.open(ConfirmSwitchDialogComponent);

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.currentView = 'list-view';
          this.filterImages();
        }
        this.filterImages();
      });
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Active':
        return 'status-active';
      case 'Expired':
        return 'status-expired';
      case 'Scheduled':
        return 'status-scheduled';
      default:
        return '';
    }
  }

  viewImageDetails(image: ImageDetails) {
  this.router.navigate(['/image-details'], { state: { selectedImage: image } });
  }
  
}

